import { state, loader, mapOptions } from './state.js';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { clusterClickHandler, renderMarkers, addMarkerCategoryFilter, showOnlyMarkers, searchMarkers } from './markers.js';
import { updateSideBar, initObserver } from './sidebar.js';
import { fetchGeoJson, loadOnlineBuisnesses } from './api.js';
import { outputModalData } from './modals.js';
import { announceMapStatus, setSidebarExpanded } from './ui.js';
import _ from 'lodash';

/**
 * Bootstrap map using async/await pattern.
 */
export async function bootstrapMap() {
	try {
		await loader.load();
		await initMap();
	} catch (err) {
		console.error('Google Maps API failed to load:', err);
	}
}

/**
 * Process URL parameters and hash to set initial map state
 */
async function processUrlState() {
	// Build category color map from filters
	document.querySelectorAll('#filters input[type="checkbox"]').forEach(cb => {
		const title = cb.dataset.pillTitle;
		const color = cb.dataset.pillColor;
		if (title && color) {
			state.categoryColors[title] = color.startsWith('#') ? color : `#${color}`;
		}
	});

	// 1. Handle Categories (?cats=12,34)
	if (state.params.cats) {
		const catIds = state.params.cats.split(',');
		addMarkerCategoryFilter(catIds);
		document.querySelector('#sidebar-wrapper').classList.remove('off-screen');
		setSidebarExpanded(true);
	}

	// 2. Handle Search (?s=keyword)
	if (state.params.s) {
		const searchInput = document.getElementById('cat-search-input');
		if (searchInput) {
			searchInput.value = state.params.s;
			const results = searchMarkers(state.params.s);
			if (results && results.length > 0) {
				state.search.markers = results;
				state.search.isActive = true;
				const pill = document.getElementById('search-pill');
				if (pill) {
					pill.style.setProperty('display', 'block', 'important');
					pill.querySelector('span').innerHTML = state.params.s;
				}
				showOnlyMarkers(results);
				document.querySelector('#sidebar-wrapper').classList.remove('off-screen');
				setSidebarExpanded(true);
				updateSideBar(true);
			}
		}
	}

	// 3. Handle specific location via hash (#location-123)
	const hash = window.location.hash;
	if (hash && hash.startsWith('#location-')) {
		const postId = hash.replace('#location-', '');
		const marker = state.markerMap.get(parseInt(postId)) || state.markerMap.get(postId);
		if (marker) {
			document.querySelector('#sidebar-wrapper').classList.remove('off-screen');
			setSidebarExpanded(true);
			outputModalData(marker);
			state.map.panTo(marker.position);
			state.map.setZoom(18);
		}
	}

	// 4. Handle Promo UI sync (?promo=slug)
	if (state.params.promo) {
		const promoButton = document.querySelector(`a[data-slug='${state.params.promo}']`);
		if (promoButton) {
			promoButton.classList.add('active');
		}
	}
}

/**
 * Main entry point: sets up the map, clusterer, listeners, and GeoJSON loading.
 */
export async function initMap() {
	let visible = true;
	let presetMarkers = [];
	
	// Import the marker library for AdvancedMarkerElement
	const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

	if (typeof state.params.promo !== 'undefined') {
		visible = false;
		let button = document.querySelector(`a[data-slug='${state.params.promo}']`);
		if (button) {
			presetMarkers = JSON.parse(button.dataset.markerIds);
		}
	}

	const mapDiv = document.getElementById("mapboxTarget");
	const loaderDiv = document.getElementById('mapboxPreloader');

	const fitMapToViewport = () => {
		const top = mapDiv.getBoundingClientRect().top + window.scrollY;
		mapDiv.style.height = (window.innerHeight - top) + 'px';
	};
	fitMapToViewport();
	window.addEventListener('resize', fitMapToViewport);

	const adjustMap = function (mode, amount) {
		switch (mode) {
			case 'tilt':
				state.map.setTilt(state.map.getTilt() + amount);
				break;
			case 'rotate':
				state.map.setHeading(state.map.getHeading() + amount);
				break;
			default:
				break;
		}
	};

	// Init Map and Clusterer
	state.map = new google.maps.Map(mapDiv, mapOptions);
	
	state.markerCluster = new MarkerClusterer({
		map: state.map,
		onClusterClick: clusterClickHandler,
		renderer: renderMarkers
	});

	// Sidebar update on idle - only add listener once
	const debouncedUpdateSidebar = _.debounce(updateSideBar, 300);
	state.map.addListener('idle', debouncedUpdateSidebar);

	// Hook the sidebar to load markers on first open
	let toggler = document.getElementById('sidebar-toggler');
	if (toggler) {
		toggler.addEventListener('click', function () {
			updateSideBar(true);
		});
	}

	// Add Rotate controls
	const buttons = [
		['<i class="fas fa-undo" aria-hidden="true"></i>', 'rotate', -25, 'TOP_RIGHT', 'Rotate map left'],
		['<i class="fas fa-redo" aria-hidden="true"></i>', 'rotate', 25, 'TOP_RIGHT', 'Rotate map right'],
		['<i class="fas fa-arrow-up" aria-hidden="true"></i>', 'tilt', 20, 'TOP_RIGHT', 'Increase map tilt'],
		['<i class="fas fa-arrow-down" aria-hidden="true"></i>', 'tilt', -20, 'TOP_RIGHT', 'Decrease map tilt']
	];

	buttons.forEach(([text, mode, amount, position, label]) => {
		const controlDiv = document.createElement('div');
		const controlUI = document.createElement('button');

		controlUI.classList.add('map-control');
		controlUI.type = 'button';
		controlUI.setAttribute('aria-label', label);
		controlUI.innerHTML = text;
		controlUI.addEventListener('click', () => {
			adjustMap(mode, amount);
		});
		controlDiv.appendChild(controlUI);
		state.map.controls[google.maps.ControlPosition[position]].push(controlDiv);
	});

	// Disable data layer click
	state.map.data.setStyle({ clickable: false });

	// Load Observer
	initObserver();

	// Load GeoJSON data
	const hideLoader = () => {
		const loaderDiv = document.getElementById('mapboxPreloader');
		if (loaderDiv && loaderDiv.style.display !== 'none') {
			loaderDiv.style.opacity = '0';
			setTimeout(() => {
				loaderDiv.style.display = 'none';
				
				// Enable search and sidebar
				const searchInput = document.getElementById('cat-search-input');
				const searchButton = document.querySelector('#cat-search .submit');
				const sidebarToggler = document.getElementById('sidebar-toggler');

				if (searchInput) {
					searchInput.removeAttribute('disabled');
					// If search input has value (e.g. from URL), enable the search button
					if (searchInput.value.length >= 3 && searchButton) {
						searchButton.removeAttribute('disabled');
						searchButton.setAttribute('aria-disabled', 'false');
					}
				}

				if (sidebarToggler) {
					sidebarToggler.style.opacity = '1';
					sidebarToggler.style.pointerEvents = 'auto';
					const sidebarTogglerButton = sidebarToggler.querySelector('button');
					if (sidebarTogglerButton) sidebarTogglerButton.removeAttribute('disabled');
				}

				// Re-enable all filter switches in the cat-contain area
				document.querySelectorAll('#cat-contain input[disabled]').forEach(el => {
					el.removeAttribute('disabled');
				});
			}, 500);
		}
	};

	// Allow testing failure state via URL parameter
	const geoJsonUrl = state.params.test_fail ? '/non-existent.json' : '/results.json';

	fetchGeoJson(geoJsonUrl).then(async data => {
		// Wait for initial tiles to load to improve perceived performance
		await new Promise(resolve => {
			google.maps.event.addListenerOnce(state.map, 'tilesloaded', resolve);
			// Safety timeout
			setTimeout(resolve, 2000);
		});

		const bounds = new google.maps.LatLngBounds();
		const markersToAdd = [];
		const CHUNK_SIZE = 100;


		// Processing in chunks to keep UI responsive
		for (let i = 0; i < data.features.length; i += CHUNK_SIZE) {
			const chunk = data.features.slice(i, i + CHUNK_SIZE);
			
			for (const feature of chunk) {
				let placeId = feature?.properties?.places_id;
				let overrides = feature?.properties?.overrides;
				
				if (!Array.isArray(overrides) && overrides?.hasOwnProperty('place_id')) {
					placeId = overrides['place_id'];
				}

				const markerLatLng = { lat: feature?.geometry?.coordinates[1], lng: feature?.geometry?.coordinates[0] };
				const color = feature?.properties?.icon_color;
				const iconURL = `/wp-content/themes/bcc_theme/images/markers/marker_${color}.png`.replace('#', '');
				
				const img = document.createElement('img');
				img.src = iconURL;
				img.style.width = '19px';
				img.style.height = '35px';
				img.className = 'custom-marker';
				img.alt = '';

				const marker = new AdvancedMarkerElement({
					position: markerLatLng,
					content: img,
					title: feature?.properties?.name,
					map: null,
				});
				
				marker.properties = {
					post_id: feature?.properties?.location_id,
					category_id: feature?.properties?.location_category,
					child_category_id: feature?.properties?.location_subcategory,
					featured_image_url: feature?.properties?.featured_image,
					category_title: feature?.properties?.category_title,
					place_id: placeId,
					social_media: feature?.properties?.social_media_links,
					keywords: feature?.properties?.keywords,
					color: color,
					is_chamber_member: feature?.properties?.is_chamber_member ?? false,
					owner_profile_id: feature?.properties?.owner_profile_id,
					owner_permalink: feature?.properties?.owner_permalink,
					chamber_profile_url: feature?.properties?.chamber_profile_url
				};
				marker.overrides = feature?.properties?.overrides;

				marker.addListener('gmp-click', function () {
					state.lastFocusedElement = document.activeElement;
					outputModalData(this);
				});

				state.markers.push(marker);
				state.markerMap.set(marker.properties.post_id, marker);
				if (visible || presetMarkers.includes(feature?.properties?.location_id)) {
					markersToAdd.push(marker);
					bounds.extend(marker.position);
				}
			}
			// Yield to main thread
			await new Promise(resolve => setTimeout(resolve, 0));
		}

		// Batch add to clusterer
		if (markersToAdd.length > 0) {
			state.markerCluster.addMarkers(markersToAdd);
		}
		
		// Sort state.markers once at the end so sidebar logic doesn't have to sort 1k items every time
		state.markers.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
		
		const sidebarWrapper = document.querySelector('#sidebar-wrapper');
		// Force update if the user opened the sidebar while data was loading
		if (sidebarWrapper && !sidebarWrapper.classList.contains('off-screen')) {
			updateSideBar(true);
		}

		// Auto-open sidebar if arriving via trigger link
		if (presetMarkers.length > 0) {
			document.querySelector('#sidebar-wrapper').classList.remove('off-screen');
			setSidebarExpanded(true);
			updateSideBar(true);
		}
		
		// Map is ready, markers are loaded, process initial URL/Hash state
		processUrlState();
		announceMapStatus(`${state.markers.length} businesses loaded. Use search, filters, or the business results list to browse the directory.`);
		
		hideLoader();
		google.maps.event.addListenerOnce(state.map, 'idle', hideLoader);
		
		// Safety timeout
		setTimeout(hideLoader, 3000);

	}).catch(error => {
		console.error('Error:', error);
		// Hide loader on error too
		hideLoader();
		
		// Show user-friendly error message
		if (window.Swal) {
			Swal.fire({
				icon: 'error',
				title: 'Oops...',
				text: 'We encountered an error loading the map data. Please try refreshing the page.',
				confirmButtonColor: '#007e00'
			});
		}
	});
}
